import { ActiveTaskCard } from '@/components/dumb/activeTaskCard';
import { AllMeetings } from '@/components/icons/allMeetings';
import { CoffeeIcon } from '@/components/icons/coffeeIcon';
import { ModalCalendar } from '@/components/icons/ModalCalendar';
import { OnWeekIcon } from '@/components/icons/onWeek';
import { PlusIcon } from '@/components/icons/plusIcon';
import { SearchIcon } from '@/components/icons/searchIcon';
import { Simplification } from '@/components/icons/Simplification';
import { TomorrowIcon } from '@/components/icons/TomorrowIcon';
import { XMarkIcon } from '@/components/icons/xMark/XMarkIcon';
import { useTaskCard } from '@/features/tasks/model/modal/useTaskCards';
import { useTaskSelectionModalLogic } from '@/features/tasks/model/modal/useTaskSelectionModalLogic';
import DesktopModal from '@/shared/ui/DesktopModal';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskDetailPath } from '@/entities/task/model/task-route';

export const TaskSelectionModalDesktop = () => {
  const logic = useTaskSelectionModalLogic();
  const navigate = useNavigate();

  const [selectedTaskId, setSelectedTaskId] = useState<string | number | null>(
    null
  );
  const [search, setSearch] = useState('');

  const openTaskModal = useCallback(
    (id: string) => {
      navigate(taskDetailPath(id));
    },
    [navigate]
  );

  useEffect(() => {
    setSelectedTaskId(null);
    setSearch('');
  }, [logic.activeTab, logic.isOpen]);

  if (!logic.isOpen) {
    return null;
  }

  const tabTasks = logic.tasksByTab[logic.activeTab];
  const query = search.trim().toLowerCase();
  const visibleTasks = query
    ? tabTasks.filter((task) => {
        const title = String(task.title ?? '').toLowerCase();
        const project = String(task.project?.Title ?? '').toLowerCase();
        return title.includes(query) || project.includes(query);
      })
    : tabTasks;

  return (
    <DesktopModal
      open={logic.isOpen}
      onClose={logic.closeModal}
      containerClassName="bg-white text-[#14222C] font-roboto shadow-[0px_0px_11px_rgba(59,76,188,0.3),0px_0px_13px_rgba(42,45,97,0.4)]"
    >
      <div className="relative isolate flex flex-col w-[1010px] gap-[40px] px-[24px] py-[44px] text-[#14222C]">
        <button
          type="button"
          onClick={logic.closeModal}
          aria-label="Закрыть"
          className="absolute right-[18px] top-[18px] z-10 flex h-[32px] w-[32px] items-center justify-center rounded-full bg-[#F8F7FC] text-[#14222C] transition hover:bg-[#ECEAF4]"
        >
          <XMarkIcon color="#14222C" width={13} height={13} />
        </button>

        {/* ===== Верх: инструкция + комментарий + действия ===== */}
        <div className="flex flex-row items-stretch justify-between gap-[40px] w-full">
          {/* Левая колонка */}
          <div className="flex flex-1 flex-col gap-[16px] min-w-0">
            {/* Инструкция */}
            <div className="rounded-[16px] bg-[#F8F7FC] shadow-[0px_3.54px_12.88px_rgba(0,0,0,0.03)] px-[14px] py-[14px]">
              <p className="m-0 text-[20px] leading-[130%] tracking-[-0.5px] text-[#14222C]">
                {logic.mode === 'intermediate-timeup'
                  ? 'Время на промежуточные дела закончилось. Обязательно опишите, что конкретно сделали — это сохранится в результат задачи, после выберите следующее действие.'
                  : logic.mode === 'complete'
                    ? 'Задача выполнена, необходимо выбрать свое следующее действие! Начинай следующую задачу либо сделай перерыв.'
                    : 'Задача приостановлена, необходимо выбрать свое следующее действие! Если переключаешься на другую задачу — заполни в текстовое поле причину.'}
              </p>
            </div>

            {/* Комментарий / результат */}
            <div className="rounded-[16px] bg-[#F8F7FC] shadow-[0px_3.54px_12.88px_rgba(0,0,0,0.03)] px-[14px] py-[14px]">
              <textarea
                value={logic.pauseComment}
                onChange={(e) => logic.setPauseComment(e.target.value)}
                placeholder={
                  logic.isIntermediateTimeup
                    ? 'Опишите результат — что конкретно сделали (обязательное поле)'
                    : 'Введите коментарий (обязательное поле для выбора одной из задач ниже)'
                }
                rows={2}
                className="w-full resize-none bg-transparent outline-none text-[14px] leading-[130%] tracking-[-0.5px] text-[#14222C] placeholder:text-[rgba(87,87,87,0.6)]"
              />
            </div>
          </div>

          {/* Правая колонка — действия */}
          <div className="flex w-[228px] shrink-0 flex-col justify-end gap-[18px]">
            {logic.mode !== 'pause' && (
              <button
                type="button"
                onClick={logic.handleResumeTask}
                disabled={logic.isRenewing || !logic.canActIntermediate}
                className="self-end text-[16px] leading-[130%] tracking-[-0.5px] underline text-[#3B4D7F] transition hover:opacity-80 disabled:opacity-40"
              >
                {logic.mode === 'intermediate-timeup'
                  ? 'Вернуться в завершенную задачу'
                  : 'Возобновить задачу'}
              </button>
            )}

            {/* Продлить план дела: работа продолжается, результат не требуем —
                план и дедлайн сдвигаются на бэке (PATCH /time) */}
            {logic.isIntermediateTimeup && (
              <ActionButton
                onClick={() => logic.handleExtendIntermediate(5)}
                disabled={logic.isBusy}
                icon={<PlusIcon color="#292D32" width={24} height={24} />}
                label="Продлить на 5 мин"
              />
            )}

            <ActionButton
              onClick={() => logic.handleBreak(15)}
              disabled={!logic.canActIntermediate}
              icon={<CoffeeIcon fill="#292D32" width={24} height={24} />}
              label="Перерыв"
            />

            <ActionButton
              onClick={logic.handleCreateIntermediate}
              disabled={logic.isBusy || !logic.canActIntermediate}
              icon={<Simplification width={24} height={24} />}
              label={logic.isBusy ? 'Загрузка…' : 'Промежутки'}
            />

            {/* Элемент из макета — пока без логики */}
            <ActionButton
              variant="green"
              icon={<PlusIcon color="#FFFFFF" width={24} height={24} />}
              label="Создать задачу"
            />
          </div>
        </div>

        {/* ===== complete / промежутки: предпросмотр следующей задачи ===== */}
        {logic.mode !== 'pause' && logic.nextTodayTask && (
          <div className="flex flex-col gap-[16px]">
            <h2 className="m-0 text-[20px] leading-[130%] tracking-[-0.5px] text-[#14222C]">
              Следующая задача по сегодняшнему плану:
            </h2>
            <ActiveTaskCard
              task={logic.nextTodayTask}
              elapsedMs={logic.spentMs}
              plannedMs={logic.plannedMs}
              assignee={logic.assignee}
              createdBy={logic.createdByUser}
              previewImage={
                logic.activePreviewImage
                  ? {
                      downloadUrl: logic.activePreviewImage.downloadUrl,
                      name: logic.activePreviewImage.name,
                    }
                  : undefined
              }
              hideActions
              onOpen={() => {
                openTaskModal(String(logic.nextTodayTask.id));
                logic.closeModal();
              }}
            />
          </div>
        )}

        {/* ===== Табы + поиск ===== */}
        <div className="flex flex-row items-center gap-[9px] w-full">
          <TabButton
            active={logic.activeTab === 'today'}
            label="Сегодня"
            icon={<ModalCalendar width={18} height={18} />}
            onClick={() => logic.setActiveTab('today')}
          />
          <TabButton
            active={logic.activeTab === 'tomorrow'}
            label="Завтра"
            icon={<TomorrowIcon width={18} height={18} />}
            onClick={() => logic.setActiveTab('tomorrow')}
          />
          <TabButton
            active={logic.activeTab === 'week'}
            label="На неделе"
            icon={<OnWeekIcon width={18} height={18} />}
            onClick={() => logic.setActiveTab('week')}
          />
          <TabButton
            active={logic.activeTab === 'deferred'}
            label="Отложенные"
            icon={<Simplification width={18} height={18} />}
            onClick={() => logic.setActiveTab('deferred')}
          />
          <TabButton
            active={logic.activeTab === 'all'}
            label="Встречи"
            icon={<AllMeetings width={18} height={18} />}
            onClick={() => logic.setActiveTab('all')}
          />

          <div className="flex flex-1 min-w-[200px] items-center gap-[12px] h-[48px] px-[18px] rounded-[24px] bg-white border border-[#EAEAEC] shadow-[0px_1px_4px_rgba(0,0,0,0.06)]">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по названии"
              className="min-w-0 flex-1 bg-transparent outline-none text-[16px] leading-[130%] tracking-[-0.5px] text-[#14222C] placeholder:text-[rgba(87,87,87,0.6)]"
            />
            <SearchIcon
              color="#6E6E6E"
              width={16}
              height={16}
              className="shrink-0"
            />
          </div>
        </div>

        {/* ===== Сетка карточек ===== */}
        <div className="w-full">
          {visibleTasks.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,469px)_minmax(0,469px)] justify-between gap-[11px]">
              {visibleTasks.map((task) => (
                <div key={task.id} className="min-w-0">
                  <TaskCardContainer
                    task={task}
                    usersMap={logic.usersMap}
                    onStart={logic.handleStartFromCard}
                    onComplete={logic.handleCompleteFromCard}
                    selectedTaskId={selectedTaskId}
                    setSelectedTaskId={setSelectedTaskId}
                    openTaskModal={openTaskModal}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[#8A8A8A] text-sm">
              {query ? 'Ничего не найдено' : 'Нет задач'}
            </div>
          )}
        </div>
      </div>
    </DesktopModal>
  );
};

const ActionButton = ({
  icon,
  label,
  onClick,
  disabled,
  variant = 'default',
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'default' | 'green';
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`
      flex h-[64px] w-full items-center justify-center gap-[12px]
      rounded-[24px] px-[18px]
      text-[18px] font-medium leading-[130%] tracking-[-0.03em]
      shadow-[0px_1px_6px_rgba(0,0,0,0.06)]
      transition disabled:cursor-not-allowed disabled:opacity-40
      ${
        variant === 'green'
          ? 'bg-[#57B792] text-white hover:bg-[#4da784]'
          : 'bg-white text-[#575757] hover:bg-[#F8F7FC]'
      }
    `}
  >
    <span className="shrink-0 flex items-center justify-center">{icon}</span>
    <span className="truncate">{label}</span>
  </button>
);

const TabButton = ({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      box-border flex items-center gap-[8px]
      h-[48px] px-[16px] rounded-[24px]
      whitespace-nowrap transition
      ${
        active
          ? 'bg-[linear-gradient(257.42deg,#152242_0.84%,#3B4D7F_100.65%)] text-white shadow-[0px_2px_8px_rgba(21,34,66,0.25)]'
          : 'bg-white text-[#000000] border border-[#EAEAEC] shadow-[0px_1px_4px_rgba(0,0,0,0.06)]'
      }
    `}
  >
    <span className="shrink-0 flex items-center justify-center">{icon}</span>
    <span className="text-[15px] leading-[130%] tracking-[-0.03em]">
      {label}
    </span>
  </button>
);

const TaskCardContainer = ({
  task,
  usersMap,
  onStart,
  onComplete,
  selectedTaskId,
  setSelectedTaskId,
  openTaskModal,
}: {
  task: ANY;
  usersMap: Map<number, ANY>;
  onStart: (task: ANY) => void;
  onComplete: (taskId: string) => void;
  selectedTaskId: string | number | null;
  setSelectedTaskId: (id: string | number | null) => void;
  openTaskModal: (id: string) => void;
}) => {
  const { elapsedMs, plannedMs, previewImage, fullTask, mapTaskToActiveCard } =
    useTaskCard(task);

  const cardProps = mapTaskToActiveCard(
    {
      ...task,
      fileStats: task.fileStats ?? fullTask?.fileStats,
    },
    usersMap
  );

  const selected = selectedTaskId === task.id;

  const handleClick = () => {
    if (selected) {
      onStart(task);
      openTaskModal(String(task.id));
      return;
    }
    setSelectedTaskId(task.id);
  };
  return (
    <ActiveTaskCard
      {...cardProps}
      selected={selected}
      onPause={handleClick}
      onComplete={() => onComplete(task.id)}
      plannedMs={plannedMs}
      elapsedMs={elapsedMs}
      previewImage={
        previewImage
          ? {
              downloadUrl: previewImage.downloadUrl,
              name: previewImage.name,
            }
          : undefined
      }
    />
  );
};
